import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { OrderSchemaType, ItemOrderSchemaType } from '../order.schema';

export const useOrderCalculations = () => {
  const { watch, setValue, getValues } = useFormContext<OrderSchemaType>();

  const items = watch('items');
  const shippingValue = watch('shippingValue') || 0;

  const calculateItemData = (item: ItemOrderSchemaType): { quantity: number; taxes: number; subtotal: number } => {
    let quantity = 0;
    let taxes = 0;
    let subtotal = 0;

    if (item.isSaved) {
      if (item.itemType === 'MATERIAL') {
        quantity += Number(item.feedstock?.quantidade) || 0;
      } else if (item.itemType === 'PRODUCT') {
        quantity += Number(item.finalProduct?.quantidade) || 0;
      } else if (item.itemType === 'RESOURCE') {
        quantity += Number(item.resource?.quantidade) || 0;
      } else if (item.itemType === 'SERVICE') {
        quantity += Number(item.stage?.quantidade) || 0;
      }

      taxes += Number(item.priceDetails?.itemTaxes || 0);
      subtotal += Number(item.priceDetails?.itemSubtotal || 0);
    }

    if (item.subItems && Array.isArray(item.subItems)) {
      for (const subItem of item.subItems) {
        const subItemData = calculateItemData(subItem);
        quantity += subItemData.quantity;
        taxes += subItemData.taxes;
        subtotal += subItemData.subtotal;
      }
    }

    return { quantity, taxes, subtotal };
  };

  const totals = useMemo(() => {
    let totalQuantity = 0;
    let totalTaxes = 0;
    let grandTotal = 0;

    if (Array.isArray(items)) {
      for (const item of items) {
        const itemData = calculateItemData(item);
        totalQuantity += itemData.quantity;
        totalTaxes += itemData.taxes;
        grandTotal += itemData.subtotal;
      }
    }
    
    grandTotal += Number(shippingValue || 0);

    return {
      calculatedTotalQuantity: totalQuantity,
      calculatedTaxes: totalTaxes,
      calculatedGrandTotal: grandTotal,
    };
  }, [items, shippingValue]);

  useEffect(() => {
    setValue('totalQuantity', totals.calculatedTotalQuantity, { shouldValidate: false });
    setValue('taxes', totals.calculatedTaxes, { shouldValidate: false });
    setValue('total', totals.calculatedGrandTotal, { shouldValidate: false });
  }, [totals, setValue]);

  return {
    totalQuantity: totals.calculatedTotalQuantity,
    taxes: totals.calculatedTaxes,
    total: totals.calculatedGrandTotal,
  };
};